// Who an item was traded with, and in which direction.
//
// The extension reads the counterparty off Steam's trade history page and stores
// it on the row as `tradedWith` = { name, profileUrl, steamId64 }. Older rows may
// hold a bare string instead, so every read goes through normalizeTradePartner.
//
// handleTradeItem in SkinTable writes `tradedWith` in two places:
//   - onto the offered row, beside `tradeId` + `tradedFor`  -> who we SENT it to
//   - onto each received row, beside `receivedFromTradeId`  -> who we GOT it from
//
// A row received in one trade and then given away in a later one gets its
// `tradedWith` overwritten by the send (SkinTable spreads tradeExtra over the
// existing row), so `tradeId` must be tested before `receivedFromTradeId` or a
// re-traded item reads as still coming "from" whoever it was sent to.

import React from 'react';

const SENT_COLOR = '#fbbf24';   // amber, matches the "Resolve" status colour
const FROM_COLOR = '#818cf8';   // indigo, matches the "Traded" status colour

// Lending lives next door in lending.js, but its badge belongs here with the
// other two so all three read as one family. lending.js re-exports the colour
// rather than defining its own, which is also what keeps the import one-way.
export const LEND_COLOR = '#38bdf8';   // sky, distinct from both of the above

/** Coerce the stored value into { name, profileUrl, steamId64 } or null. */
export function normalizeTradePartner(tradedWith) {
  if (!tradedWith) return null;

  const p = typeof tradedWith === 'string'
    ? { name: tradedWith, profileUrl: null, steamId64: null }
    : tradedWith;

  let name = String(p.name ?? '').trim();

  // Steam persona names frequently arrive wrapped in their own quote characters
  // ("KrisGFX"), which reads as a rendering bug inside a badge. Only a matched
  // pair is stripped, so a name that genuinely contains one quote is untouched.
  const first = name[0];
  const last  = name[name.length - 1];
  if (name.length > 1 && (first === '"' || first === "'") && first === last) {
    name = name.slice(1, -1).trim();
  }

  if (!name) return null;
  return { name, profileUrl: p.profileUrl || null, steamId64: p.steamId64 || null };
}

/** Partner plus direction for a tracker row, or null when there is nothing to show. */
export function tradePartnerOf(row) {
  const partner = normalizeTradePartner(row?.tradedWith);
  if (!partner) return null;
  if (row.tradeId)             return { ...partner, direction: 'sent' };
  if (row.receivedFromTradeId) return { ...partner, direction: 'from' };
  return null;
}

/** Chip descriptor for card layouts that render their own chip list. */
export function tradePartnerChip(row) {
  const partner = tradePartnerOf(row);
  if (!partner) return null;
  return {
    label: `${partner.direction === 'sent' ? 'sent to' : 'from'} ${partner.name}`,
    color: partner.direction === 'sent' ? SENT_COLOR : FROM_COLOR,
  };
}

const EXTERIOR_IN_NAME = /\s*\((Factory New|Minimal Wear|Field[- ]Tested|Well[- ]Worn|Battle[- ]Scarred)\)\s*$/i;

/** Collapse "Field-Tested" / "Field Tested" / null to one comparable token. */
function condToken(value) {
  return String(value ?? '').toLowerCase().replace(/[^a-z]/g, '');
}

/** Key a tracker row and an inventory item onto the same string. */
function matchKey(name, condition) {
  const base = String(name ?? '').replace(EXTERIOR_IN_NAME, '').trim().toLowerCase();
  if (!base) return null;
  return `${base}|${condToken(condition)}`;
}

function inventoryKey(item) {
  const mhn = item.marketHashName || item.market_hash_name || '';
  const ext = mhn.match(EXTERIOR_IN_NAME);
  return matchKey(mhn, item.exterior || (ext ? ext[1] : null));
}

/**
 * Build an assetId -> partner map for the inventory cards.
 *
 * Matching by assetId alone is not enough. Steam's trade history never reveals
 * the assetId an item receives once it lands in your inventory, so rows created
 * from a received trade usually carry assetId: null (tradeHistory.js falls back
 * to the float ledger, which only helps when the item was seen before). Those
 * are exactly the rows worth showing, since the items are still in the locker.
 *
 * So unmatched rows fall back to name + exterior. The fallback is bounded by how
 * many rows actually carry that key: if one "Pros Don't Fake" came from a trade
 * and three sit in the inventory, exactly one is tagged, never all three. Which
 * of the identical copies gets picked is arbitrary (lowest assetId, for a stable
 * render), but the count is always right.
 */
export function buildTradePartnerMap(rows, inventoryItems) {
  const map = new Map();
  const inventory = inventoryItems || [];

  const partnered = [];
  for (const row of rows || []) {
    const partner = tradePartnerOf(row);
    if (partner) partnered.push({ row, partner });
  }

  // Pass 1: exact assetId, the only unambiguous link.
  const claimed = new Set();
  const leftover = [];
  for (const entry of partnered) {
    if (entry.row.assetId && !claimed.has(entry.row.assetId)) {
      map.set(entry.row.assetId, entry.partner);
      claimed.add(entry.row.assetId);
    } else {
      leftover.push(entry);
    }
  }
  if (leftover.length === 0 || inventory.length === 0) return map;

  // Pass 2: name + exterior, capped at the number of rows holding each key.
  const pool = new Map();
  for (const item of inventory) {
    if (!item?.assetId || claimed.has(item.assetId)) continue;
    const key = inventoryKey(item);
    if (!key) continue;
    if (!pool.has(key)) pool.set(key, []);
    pool.get(key).push(item.assetId);
  }
  for (const ids of pool.values()) ids.sort();

  for (const { row, partner } of leftover) {
    const key = matchKey(row.skin?.value, row.condition?.value);
    const ids = key && pool.get(key);
    if (!ids?.length) continue;
    const assetId = ids.shift();
    map.set(assetId, partner);
    claimed.add(assetId);
  }

  return map;
}

/**
 * Collapse repeated syncs down to distinct trades.
 *
 * The stored history appends on every sync without deduping, so the same trade
 * shows up many times over (1177 stored events collapse to 172 real ones). Left
 * raw it would skew any count-based matching badly.
 */
function dedupeEvents(events) {
  const seen = new Set();
  const out  = [];
  for (const e of events || []) {
    if (!e) continue;
    const sig = JSON.stringify([
      e.timestamp, e.direction,
      (e.itemsGiven    || []).map(i => i?.name).sort(),
      (e.itemsReceived || []).map(i => i?.name).sort(),
      e.tradedWith?.steamId64 ?? e.tradedWith?.name ?? null,
    ]);
    if (seen.has(sig)) continue;
    seen.add(sig);
    out.push(e);
  }
  return out;
}

/**
 * key -> partner, built from received trade events.
 *
 * Only unambiguous keys survive. An item name that arrived from several different
 * people (plenty of duplicate skins do) is dropped rather than attributed to
 * whichever event happened to be seen first, since a confidently wrong name is
 * worse here than no name at all.
 */
export function buildHistoryPartnerIndex(tradeHistory) {
  const seen = new Map();

  for (const event of dedupeEvents(tradeHistory)) {
    const partner = normalizeTradePartner(event.tradedWith);
    if (!partner) continue;

    for (const item of event.itemsReceived || []) {
      const key = matchKey(item?.name, item?.condition);
      if (!key) continue;
      const prev = seen.get(key);
      if (!prev) {
        seen.set(key, { partner, ambiguous: false });
      } else if (
        prev.partner.name !== partner.name ||
        prev.partner.steamId64 !== partner.steamId64
      ) {
        prev.ambiguous = true;
      }
    }
  }

  const index = new Map();
  for (const [key, v] of seen) {
    if (!v.ambiguous) index.set(key, { ...v.partner, direction: 'from' });
  }
  return index;
}

/**
 * The single lookup the UI uses.
 *
 * `tradedWith` is only ever written by handleTradeItem, so an item that entered
 * the tracker any other way (a manual add, or an inventory import that read it as
 * a new purchase) has no partner on its row even when the extension captured the
 * trade perfectly well. The history index covers exactly that gap.
 */
export function buildTradePartnerIndex({ rows, inventoryItems, tradeHistory }) {
  const history   = buildHistoryPartnerIndex(tradeHistory);
  const byAssetId = buildTradePartnerMap(rows, inventoryItems);

  // Inventory items no row accounted for, but the history can name.
  for (const item of inventoryItems || []) {
    if (!item?.assetId || byAssetId.has(item.assetId)) continue;
    const key = inventoryKey(item);
    const partner = key && history.get(key);
    if (partner) byAssetId.set(item.assetId, partner);
  }

  return {
    byAssetId,
    forRow(row) {
      const own = tradePartnerOf(row);
      if (own) return own;
      // Linked to a trade but with no partner recorded: nothing to infer from.
      if (row?.tradeId || row?.receivedFromTradeId) return null;
      const key = matchKey(row?.skin?.value, row?.condition?.value);
      return (key && history.get(key)) || null;
    },
  };
}

function PartnerName({ partner, color }) {
  if (!partner.profileUrl) return <>{partner.name}</>;
  return (
    <a
      href={partner.profileUrl}
      target="_blank"
      rel="noreferrer"
      onClick={e => e.stopPropagation()}
      style={{ color, textDecoration: 'underline' }}
    >
      {partner.name}
    </a>
  );
}

/**
 * Pill badge for card and row layouts.
 *
 * Pass either `row` (a tracker row) or `partner` (an already-resolved partner,
 * which is what the inventory lookup map hands back).
 *
 * `flat` drops the rounding for the Zone theme's square styling.
 */
export function TradePartnerBadge({ row, partner: partnerProp, index, flat = false, style }) {
  const partner = partnerProp || (index ? index.forRow(row) : tradePartnerOf(row));
  if (!partner) return null;

  const sent  = partner.direction === 'sent';
  const color = sent ? SENT_COLOR : FROM_COLOR;

  return (
    <span
      title={sent ? `Sent to ${partner.name}` : `Received from ${partner.name}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        maxWidth: '100%',
        fontSize: '0.9rem',
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        padding: '1px 7px',
        borderRadius: flat ? 0 : 999,
        color,
        background: sent ? 'rgba(251,191,36,0.15)' : 'rgba(129,140,248,0.12)',
        border: `1px solid ${sent ? 'rgba(251,191,36,0.35)' : 'rgba(129,140,248,0.28)'}`,
        ...style,
      }}
    >
      {sent ? 'sent to' : 'from'} <PartnerName partner={partner} color={color} />
    </span>
  );
}

/**
 * "This one is out on loan."
 *
 * Lent rows stay in Live and in every total, because a lend changes nothing
 * about what you own. That is correct but invisible, so the item you cannot
 * currently touch has to say so wherever it is listed.
 */
export function LentBadge({ row, flat = false, style }) {
  const lend = row?.lend;
  const name = lend && normalizeTradePartner(lend.to)?.name;
  if (!name) return null;

  return (
    <span
      title={`Lent to ${name}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        maxWidth: '100%',
        fontSize: '0.9rem',
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        padding: '1px 7px',
        borderRadius: flat ? 0 : 999,
        color: LEND_COLOR,
        background: 'rgba(56,189,248,0.13)',
        border: `1px solid ${flat ? '#131313' : 'rgba(56,189,248,0.35)'}`,
        ...style,
      }}
    >
      lent to {name}
    </span>
  );
}

/** Borderless single line, for the roomier trade-flow layouts. */
export function TradePartnerLine({ row, partner: partnerProp, index, label, style }) {
  const partner = partnerProp || (index ? index.forRow(row) : tradePartnerOf(row));
  if (!partner) return null;

  const color = partner.direction === 'sent' ? SENT_COLOR : FROM_COLOR;
  const text  = label ?? (partner.direction === 'sent' ? 'with' : 'from');

  return (
    <div
      style={{
        fontSize: '0.9rem',
        fontWeight: 600,
        color,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {text} <PartnerName partner={partner} color={color} />
    </div>
  );
}
